import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, effect, ElementRef, EventEmitter, inject, Input, OnInit, Output, signal, untracked, ViewChild, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, FormsModule, NgModel, ValidationErrors } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { CouponDbModel } from '@app/models/coupon-db.model';
import { CAPTCHA_TYPE, PAYMENT_TYPES } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { PayPalSettingsDbModel } from '@models/partner-setting.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioAddRequiredPipe } from '@pipes/calio-add-required.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { AdyenResumeService } from '@services/feature/adyen-resume.service';
import { CaptchaService } from '@services/feature/captcha.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { WidgetService } from '@services/feature/widget.service';
import { buildAdyenBookingContext } from '@services/utils/adyen-booking-context.util';
import { MountedFriendlyCaptcha, mountFriendlyCaptcha } from '@services/utils/friendly-captcha-widget.util';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { resolvePreselectedPaymentType } from '@services/utils/payment-preselection.util';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { CouponFormComponent } from '@ui-lib/coupon-form/coupon-form.component';
import { CwAdyenComponent } from '@ui-lib/cw-adyen/cw-adyen.component';
import { CwPaypalComponent } from '@ui-lib/cw-paypal/cw-paypal.component';
import { StripeComponent } from '@ui-lib/stripe/stripe.component';
import { CswLinkComponent } from '@ui-lib/typography/csw-link/csw-link.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { InvoiceAddressFormComponent } from '@ui-lib/invoice-address-form/invoice-address-form.component';
import { AdyenPaymentDetails } from '@models/adyen.model';
import { InvoiceAddress } from '@models/invoice-address.model';
import { courtesyKey } from '@services/utils/courtesy-key.util';
import { UserInfo } from '@models/user.model';
import { Observable, debounceTime, finalize, of, switchMap } from 'rxjs';

@Component({
  selector: 'app-payment-method-view',
  templateUrl: './payment-method-view.component.html',
  styleUrls: ['./payment-method-view.component.scss'],
  imports: [CswTextComponent, FormsModule, CswLinkComponent, CouponFormComponent, StripeComponent, CwPaypalComponent, CwAdyenComponent, ButtonComponent, TranslateModule, CalioSafePipe, CalioAddRequiredPipe, CalioTranslationPipe, CalioMeetingTemplatePipe, MatIconModule, InvoiceAddressFormComponent]
})
export class PaymentMethodViewComponent implements OnInit {

  private captchaService = inject(CaptchaService);
  private adyenResumeService = inject(AdyenResumeService);
  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  private helperService = inject(HelperService);
  private translateService = inject(TranslateService);
  public bookingService = inject(BookingService);
  public widgetService = inject(WidgetService);

  @Input() coupon: CouponDbModel;
  @Input() finalCouponDiscount: number;
  @Input() originalAmount: number;
  @Input() priceAfterCouponReduction: number;
  @Input() storageComplete = false;

  @Output() previousPageEvent = new EventEmitter<void>();
  @Output() onAdditionalServiceEvent = new EventEmitter<void>();

  @ViewChild('captcha') captchaModel: NgModel;

  protected readonly captchaType = CAPTCHA_TYPE;
  protected readonly paymentMethods = PAYMENT_TYPES;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  private isOnlinePaymentMandatory = false;
  protected captchaInput: string;
  protected disableAllButton = false;
  protected errorMessage: string;
  protected errorTitle: string;
  protected isBexioEnabled = false;
  protected isLexOfficePaymentTypeEnabled = false;
  protected isExternalInvoiceEnabled = false;
  protected isPaypalEnabled = false;
  protected isAdyenEnabled = false;
  protected isServiceOnlinePaymentEnabled = false;
  protected isStoreEnabled = false;
  protected isStripeEnabled = false;
  protected lang: string;
  protected paymentType: string;
  protected paypalSetting: PayPalSettingsDbModel;
  protected showCouponField = false;
  protected showError = false;
  protected showPaypalLoader = false;
  protected stripeConnectedAccountId: string;
  protected validatingCaptchaChallenge = false;
  /** The Drop-in is loading a checkout: Book would reach nothing, so it waits. */
  protected readonly adyenCheckoutLoading = signal(false);
  /**
   * A refused booking spent the Friendly Captcha answer the personal form collected, so a fresh one
   * is asked for here, beside Book. Stays up once shown; each new refusal re-mounts it.
   */
  protected readonly friendlyCaptchaRenewal = signal(false);
  private readonly friendlyCaptchaRenewalRound = signal(0);
  private readonly friendlyCaptchaRenewalHost = viewChild<ElementRef<HTMLElement>>('friendlyCaptchaRenewal');
  private renewalCaptcha: MountedFriendlyCaptcha | null = null;

  constructor() {
    this.lang = this.translateService.getFallbackLang();
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);

    // (Re-)mount the renewal captcha whenever its container appears or another refusal asks for a
    // new answer - the old widget holds a spent one, so it is destroyed rather than reused.
    effect(() => {
      const host = this.friendlyCaptchaRenewalHost();
      this.friendlyCaptchaRenewalRound();
      untracked(() => this.mountRenewalCaptcha(host?.nativeElement));
    });
    this.destroyRef.onDestroy(() => this.mountRenewalCaptcha(undefined));
  }

  ngOnInit(): void {
    this.customEventService.stripeSuccessfulEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.resetError();
      this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId = result;
      this.book();
    });

    this.customEventService.resetStripeTokenEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.resetError();
      this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId = undefined;
      this.disableAllButton = false;
      // storageComplete is now controlled by the parent component via @Input()
    });

    this.customEventService.resetBookingButtonsEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.disableAllButton = false;
    });

    this.customEventService.paypalStartedEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Popup open, payment in progress: same mechanism as the Stripe path, where pressing Book
      // sets disableAllButton for the whole attempt — a mid-payment cart or navigation change
      // would desync the amount PayPal is about to capture. paypalFailedEvent clears it again,
      // exactly like resetStripeTokenEvent does for a failed Stripe attempt.
      this.resetError();
      this.disableAllButton = true;
    });

    this.customEventService.paypalSuccessfulEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => {
      this.resetError();
      this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail = result;
      // PayPal CAPTURES the money in the approval popup, before Book is ever pressed — leaving
      // the customer on the summary with a completed payment and no appointment. Submit the
      // booking automatically, through the FULL manual-press path (submitBooking, not book):
      // it carries the captcha gate, so a calenso_default-captcha partner still gets the
      // recoverable "Please complete the captcha" banner INSTEAD of a backend rejection
      // after the money is already captured.
      this.submitBooking();
    });

    this.customEventService.paypalFailedEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(reason => {
      this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail = undefined;
      this.disableAllButton = false;
      this.setError(
        reason === 'cancel'
          ? 'The PayPal payment was cancelled before completion. Please try again.'
          : 'The PayPal payment failed. Please try again or choose another payment method.',
        'Booking Error'
      );
    });

    this.customEventService.adyenPaymentStartedEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      // Pay pressed inside the Drop-in: freeze the summary for the whole authorisation, exactly
      // as paypalStartedEvent does for the PayPal popup. resetAdyenEvent (refused / cancelled /
      // failed) clears it again; an authorised payment keeps the lock through paymentLocked.
      this.resetError();
      this.disableAllButton = true;
    });

    this.customEventService.adyenSuccessfulEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(result => this.onAdyenAuthorised(result));

    this.customEventService.resetAdyenEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(errorKey => {
      this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails = undefined;
      this.disableAllButton = false;
      if (errorKey) {
        this.setError(this.translateService.instant(errorKey), 'Booking Error');
      }
    });

    this.customEventService.resetPaypalEvent.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.resetError();
      this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail = undefined;
      this.showPaypalLoader = true;
      setTimeout(() => {
        this.showPaypalLoader = false;
      }, 4000);
    });

    this.customEventService.adyenCheckoutLoadingChanged.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(loading => this.adyenCheckoutLoading.set(loading));

    this.customEventService.friendlyCaptchaRenewalRequested.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.friendlyCaptchaRenewal.set(true);
      this.friendlyCaptchaRenewalRound.update(round => round + 1);
    });
    // Re-created after "Pay again" with the refused booking's answer already cleared.
    if (this.isFriendlyCaptchaPartner && !this.bookingService.selectedBookingData?.selectedUser?.friendly_captcha) {
      this.friendlyCaptchaRenewal.set(true);
    }

    this.checkForCaptcha();

    this.verifyOnlinePaymentStatus();

    if (this.widgetService.widgetConf?.context?.coupon) {
      this.showCouponField = true;
    }
  }

  private checkForCaptcha(): void {
    this.bookingService.selectedBookingData.captcha = undefined;
    this.bookingService.selectedBookingData.selectedUser.captcha = '';

    if (
      this.bookingService.partnerData?.is_captcha_feature_enabled &&
      this.bookingService.partnerData?.captcha_type === CAPTCHA_TYPE.CALENSO_DEFAULT
    ) {
      this.captchaService.generateCaptcha().subscribe({
        next: captcha => {
          if (captcha) {
            this.bookingService.selectedBookingData.captcha = captcha;
            setTimeout(() => {
              this.captchaModel.control.setValue('');
              this.captchaModel.control.addAsyncValidators(this.captchaValidator());
            }, 500);
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
    }
  }

  private captchaValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      this.validatingCaptchaChallenge = true;

      return this.captchaService.validateCaptcha(
        this.bookingService.selectedBookingData.captcha.uuid,
        control.value
      ).pipe(
        debounceTime(2000),
        switchMap(result => {
          control.markAsTouched();
          if (!result) {
            return of({ invalidCaptcha: true });
          }
          this.bookingService.selectedBookingData.selectedUser.captcha = control.value;
          return of(null);
        }),
        finalize(() => this.validatingCaptchaChallenge = false)
      );
    };
  }

  private get isFriendlyCaptchaPartner(): boolean {
    return !!Number(this.bookingService.partnerData?.is_captcha_feature_enabled)
      && this.bookingService.partnerData?.captcha_type === CAPTCHA_TYPE.FRIENDLY;
  }

  /** The renewal hint, in the partner's courtesy form. */
  protected get friendlyCaptchaRenewalHintKey(): string {
    return courtesyKey('captcha.renewalHint', this.widgetService.widgetConf?.partner?.courtesy_form);
  }

  private mountRenewalCaptcha(element: HTMLElement | undefined): void {
    this.renewalCaptcha?.destroy();
    this.renewalCaptcha = null;
    const sitekey = this.bookingService.partnerData?.friendly_captcha_sitekey;
    if (!element || !sitekey) {
      return;
    }
    this.renewalCaptcha = mountFriendlyCaptcha({
      element,
      sitekey,
      language: (this.lang ?? '').split('_')[0],
      webComponent: this.widgetService.isWebComponentMode,
      onComplete: response => {
        this.bookingService.selectedBookingData.selectedUser.friendly_captcha = response;
        this.resetError();
      },
      onError: error => {
        this.bookingService.selectedBookingData.selectedUser.friendly_captcha = '';
        LoggerService.warn('Captcha error:', error);
      },
    });
  }

  /**
   * An Adyen payment is authorised and about to be booked. cakephp checks the captcha for such a
   * booking only after it has verified and claimed the paid session, and waives it then - so the
   * browser must not stop it first: after a redirect or a same-tab hosted payment the page is new
   * and the single-use answer is gone, and a captcha gate here would leave the shopper charged with
   * no booking. Any other booking - unpaid, or another method - still needs the captcha.
   */
  private isAuthorisedAdyenPayment(): boolean {
    const paid = this.bookingService.selectedBookingData?.paymentMethodData;
    return this.paymentType === this.paymentMethods.ADYEN
      && paid?.paymentType === this.paymentMethods.ADYEN
      && !!paid?.adyenPaymentDetails?.session_id
      && !!paid?.adyenPaymentDetails?.session_result;
  }

  isCaptchaValidated(): boolean {
    // A Friendly Captcha answer is single-use: after a refused booking the payment step holds none
    // until the renewal captcha beside Book is solved, and submitting without one only buys a
    // refusal - after the card was charged.
    if (this.isFriendlyCaptchaPartner && this.friendlyCaptchaRenewal()
      && !this.bookingService.selectedBookingData?.selectedUser?.friendly_captcha) {
      return false;
    }
    if (
      this.bookingService.partnerData?.is_captcha_feature_enabled &&
      this.bookingService.partnerData?.captcha_type === CAPTCHA_TYPE.CALENSO_DEFAULT
    ) {
      return this.bookingService.selectedBookingData.selectedUser.captcha ? true : false;
    }
    return true;
  }

  verifyOnlinePaymentStatus(): void {
    // Asked about THIS booking - the same context the Adyen session is minted with - so
    // `adyenEnabled` says whether the booked worker's store can take the money, not merely whether
    // some store of the partner has an Adyen account.
    const bookingContext = buildAdyenBookingContext(this.bookingService.selectedBookingData);
    this.bookingService.verifyOnlinePaymentStatus(bookingContext).subscribe({
      next: result => {
        if (result) {
          this.isServiceOnlinePaymentEnabled = !!result.isServiceOnlinePaymentEnabled;
          if (
            result.isServiceOnlinePaymentEnabled === 1 &&
            result.isStripeAppointmentPaymentEnabled === 1 &&
            result.stripeEnabled
          ) {
            this.isStripeEnabled = true;
            this.helperService.loadStripeJs();
          }
          if (result.isOnlinePaymentMandatory === 1) {
            this.isOnlinePaymentMandatory = true;
          }
          if (result.isBexioAppointmentServicePaymentEnabled === 1 && result.bexioEnabled) {
            this.isBexioEnabled = true;
          }
          if (result.adyenEnabled) {
            this.isAdyenEnabled = true;
          }
          if (result.paypalEnabled) {
            this.isPaypalEnabled = true;
            this.paypalSetting = new PayPalSettingsDbModel();
            this.paypalSetting.access_token = result.paypalAccessToken;
            this.paypalSetting.client_id = result.paypalClientId;
          }
          if (result.onlinePaymentShowOnSitePaymentType === 1 && result.isOnlinePaymentMandatory !== 1) {
            this.isStoreEnabled = true;
          }

          this.isLexOfficePaymentTypeEnabled = (result.lexOfficeEnabled === true);
          // no second condition, unlike Bexio: this mode needs no provider connected
          this.isExternalInvoiceEnabled = (result.isExternalInvoiceAppointmentPaymentEnabled === 1);
          this.stripeConnectedAccountId = result.stripeConnectedAccountId ?? null;
        }
        // A PayPal capture outlives this component (it is held on the booking service): if the
        // summary is re-mounted with one, the customer has already paid via PayPal, the radios are
        // locked by that capture, and the only booking that can be made is the PayPal one.
        this.paymentType = this.bookingService.selectedBookingData?.paymentMethodData?.paypalOrderDetail
          ? this.paymentMethods.PAYPAL
          : this.getPreselectedPaymentType(result?.defaultPaymentType);
        // The service-level selection outlives this view. An UNPAID Adyen selection left there
        // (the customer chose Adyen, went back, returned) would keep the summary's cart frozen
        // while no radio is checked and no Drop-in is shown. Both halves must agree.
        const paid = this.bookingService.selectedBookingData?.paymentMethodData;
        if (paid?.paymentType === this.paymentMethods.ADYEN && !paid?.adyenPaymentDetails) {
          this.setSelectedPaymentType(undefined);
        }
        // Back from an Adyen payment that left the page: its pane must mount to finish it, whatever
        // the partner's default method is.
        if (this.adyenResumeService.hasPendingResume() && this.isServiceOnlinePaymentEnabled && this.isAdyenSelectable) {
          this.paymentType = this.paymentMethods.ADYEN;
        }
        if (this.paymentType) {
          this.setSelectedPaymentType(this.paymentType);
        }
      },
      error: (error: HttpErrorResponse) => LoggerService.error(error)
    });
  }

  private getPreselectedPaymentType(defaultPaymentType: string): string {
    const enabledPaymentTypes = [
      this.isServiceOnlinePaymentEnabled && this.isStripeEnabled ? this.paymentMethods.CREDITCARD : null,
      this.isBexioEnabled ? this.paymentMethods.INVOICE : null,
      this.isServiceOnlinePaymentEnabled && this.isPaypalEnabled ? this.paymentMethods.PAYPAL : null,
      this.isServiceOnlinePaymentEnabled && this.isAdyenSelectable ? this.paymentMethods.ADYEN : null,
      this.isStoreEnabled ? this.paymentMethods.STORE : null,
      this.isLexOfficePaymentTypeEnabled ? this.paymentMethods.LEX_OFFICE_INVOICE : null,
      this.isExternalInvoiceEnabled ? this.paymentMethods.EXTERNAL_INVOICE : null
    ].filter(Boolean);

    return resolvePreselectedPaymentType(enabledPaymentTypes, defaultPaymentType);
  }

  onPaymentTypeChange(event: string): void {
    this.resetError();
    this.paymentType = event;
    this.setSelectedPaymentType(event);
    this.disableAllButton = false;
  }

  previous(): void {
    this.previousPageEvent.emit();
  }

  bookAdditionalService(): void {
    this.onAdditionalServiceEvent.emit();
  }

  /**
   * Adyen authorised - from Drop-in, from the hosted popup (its message or its poll) or from a
   * same-tab return. Each of those hands one result on, once. The payment is now claimable exactly
   * once, so book immediately rather than leaving the customer on a form whose session could expire
   * underneath them. Through the FULL manual-press path (submitBooking, not book), exactly as the
   * PayPal capture does: whatever would stop a pressed Book stops this one too and leaves Book
   * enabled with the payment kept, and a booking that goes out disables Book until it is answered.
   */
  private onAdyenAuthorised(result: AdyenPaymentDetails): void {
    this.resetError();
    this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails = result;
    this.submitBooking();
  }

  submitBooking(): void {
    if (this.isAuthorisedAdyenPayment() || this.isCaptchaValidated()) {
      // When a 100% coupon reduces the price to 0, switch to STORE payment
      // to avoid triggering Stripe when the payment element is hidden.
      if (this.bookingService?.selectedBookingData?.totalBookingAmount <= 0) {
        this.paymentType = this.paymentMethods.STORE;
        this.setSelectedPaymentType(this.paymentType);
      }

      // Adyen is offered only for a single-booking checkout. If the cart grew after it was chosen,
      // the radio and the Drop-in are gone but the selection is not - and the Adyen branch below
      // would emit to zero listeners: a Book press that silently does nothing. Drop the stale
      // selection so the existing "Please select a payment method" path speaks instead.
      if (this.paymentType === this.paymentMethods.ADYEN && !this.isAdyenSelectable) {
        this.paymentType = undefined;
        this.setSelectedPaymentType(undefined);
      }

      if (this.paymentType === this.paymentMethods.CREDITCARD) {
        this.disableAllButton = true;
        this.customEventService.stripePaymentFlowRequested.emit();
      } else if (
        this.paymentType === this.paymentMethods.ADYEN &&
        !this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails
      ) {
        // Book IS the pay button now: Drop-in renders without one (showPayButton: false),
        // so this signal asks it to charge the card, exactly as the CREDITCARD branch above
        // asks Stripe. The booking resumes on adyenSuccessfulEvent.
        //
        // No error is set here. An incomplete card is not a failure - Drop-in marks its own
        // fields and the customer finishes them - and the old "not completed" message now
        // describes a button the customer can no longer press.
        //
        // Deliberately does NOT set disableAllButton, unlike the CREDITCARD branch above.
        // Stripe always calls back, so disabling there is safe. An Adyen submit on an
        // INCOMPLETE card is a no-op: Adyen marks its own fields and emits neither
        // adyenSuccessfulEvent nor resetAdyenEvent - and resetAdyenEvent is the only thing
        // that clears the flag. Disabling here therefore left the shopper with a dead Book
        // button and no way to finish the card. A completed payment locks the button anyway
        // via paymentLocked (adyenPaymentDetails), so nothing is lost by leaving it enabled.
        this.customEventService.adyenPaymentFlowRequested.emit();
      } else {
        this.book();
      }
    } else {
      this.setError('Please complete the captcha', 'Booking Error');
      // The attempt freeze must not outlive a submit that never left the browser. After a PayPal
      // capture this is the auto-submit landing on an unsolved captcha: the money is taken, so the
      // customer must be able to solve it and press Book — otherwise the summary is frozen for good
      // with a payment and no appointment. The capture itself keeps the payment method and coupon
      // locked (paymentLocked), so nothing PayPal charged for can change in between.
      this.disableAllButton = false;
    }
  }

  /**
   * Adyen may be offered only when the backend enabled it AND this checkout produces exactly one
   * booking.
   *
   * One session pays ONE booking. With several services in the cart the session is minted for the
   * cart TOTAL while cakephp validates each booking against its own amount - it throws "The paid
   * amount does not match the booking amount." - so the card is charged and the booking then
   * fails. The widget has refused multi-item carts for this reason since the feature landed
   * (cw-payment-methods `isAdyenSelectable`); this is the same guard.
   *
   * A getter rather than a flag set once, because the cart is still editable on the summary:
   * "Add service" can turn a single-service checkout into a multi-service one after the payment
   * methods have loaded, and a latched flag would keep offering Adyen.
   */
  protected get isAdyenSelectable(): boolean {
    return this.isAdyenEnabled
      && (this.bookingService?.selectedBookingData?.cartItems?.length ?? 0) <= 1;
  }

  /**
   * The payment method and the coupon are locked while an attempt is in flight AND once a PayPal
   * capture exists: PayPal has already taken the money for this exact amount, so switching the
   * method or applying a coupon afterwards would desync what was charged from what is booked. The
   * Book button follows only the in-flight freeze, so a capture that landed on an unsolved captcha
   * or a rejected booking can still be submitted again with the same capture.
   */
  protected get paymentLocked(): boolean {
    const paid = this.bookingService?.selectedBookingData?.paymentMethodData;
    // An authorised Adyen payment locks for the same reason a PayPal capture does: the money is
    // taken for this exact amount, so the method and the coupon must not change underneath it.
    return this.disableAllButton || !!paid?.paypalOrderDetail || !!paid?.adyenPaymentDetails;
  }

  book(): void {
    if (this.isPaymentTypeValidated()) {
      this.disableAllButton = true;
      this.customEventService.doBookingEvent.emit();
    } else if (!this.paymentType) {
      this.setError('Please select a payment method', 'Booking Error');
    } else {
      this.setError('Something went wrong. Please try again.', 'Booking Error');
      this.disableAllButton = false;
    }
  }

  setSelectedPaymentType(paymentType: string): void {
    const data = this.bookingService.selectedBookingData.paymentMethodData;
    data.paymentType = paymentType;

    // Selecting a type re-creates the invoice form, so its state starts over too: the address never
    // rides along on another type, and a Back round-trip cannot re-send an address (or keep a
    // half-filled form's lock on the book button) that the fresh form no longer shows.
    data.invoiceAddress = null;
    data.invoiceAddressValid = true;
  }

  /** The invoice address may only be optional when the backend has a complete address to snapshot.
   * The booking form showing the fields is not enough — they are required only when
   * `customer_required` also lists `address`, so a shown-but-blank address would be snapshotted
   * empty. Keying on what the booker actually entered covers both cases. */
  protected get isInvoiceAddressMandatory(): boolean {
    const fields: (string | number)[] = this.widgetService.widgetConf?.context?.customer ?? [];
    if (!fields.includes('address')) {
      return true;
    }

    const user = this.bookingService.selectedBookingData?.selectedUser;

    return !(user?.street?.trim() && user?.zip?.trim() && user?.city?.trim());
  }

  protected get invoicePrefill(): UserInfo | null {
    return this.bookingService.selectedBookingData?.selectedUser ?? null;
  }

  protected get partnerCountryCode(): string {
    return this.bookingService.partnerData?.country?.identifier ?? '';
  }

  protected get isInvoiceAddressComplete(): boolean {
    return this.bookingService.selectedBookingData?.paymentMethodData?.invoiceAddressValid !== false;
  }

  /** Gates the book button: only while invoice is the chosen type and its form is incomplete. */
  protected get isInvoiceAddressIncomplete(): boolean {
    return this.paymentType === this.paymentMethods.EXTERNAL_INVOICE && !this.isInvoiceAddressComplete;
  }

  onInvoiceAddressChange(event: { checked: boolean; valid: boolean; value: InvoiceAddress | null }): void {
    const data = this.bookingService.selectedBookingData.paymentMethodData;
    data.invoiceAddress = event.value;
    data.invoiceAddressValid = event.valid;
  }

  isPaymentTypeValidated(): boolean {
    if (this.bookingService?.selectedBookingData?.totalBookingAmount <= 0) {
      this.paymentType = this.paymentMethods.STORE;
      this.setSelectedPaymentType(this.paymentType);
      return true;
    }

    // isAdyenSelectable, not isAdyenEnabled: on a multi-item cart Adyen is not offered, so it must
    // not count as an available method here either - otherwise this branch decides some online
    // method exists, skips the fall-through to STORE, and the customer is left on a payment step
    // with nothing selectable.
    if (!this.isBexioEnabled && !this.isStripeEnabled && !this.isPaypalEnabled && !this.isAdyenSelectable && !this.isLexOfficePaymentTypeEnabled && !this.isExternalInvoiceEnabled) {
      // Add comment online payment is not active
      this.paymentType = this.paymentMethods.STORE;
      this.setSelectedPaymentType(this.paymentType);
      return true;
    } else {
      if (this.isOnlinePaymentMandatory) {
        if (
          (
            this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId &&
            this.paymentType === this.paymentMethods.CREDITCARD
          ) ||
          this.paymentType === this.paymentMethods.INVOICE ||
          (this.paymentType === this.paymentMethods.EXTERNAL_INVOICE && this.isInvoiceAddressComplete) ||
          (
            this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail &&
            this.paymentType === this.paymentMethods.PAYPAL
          ) ||
          (
            this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails &&
            this.paymentType === this.paymentMethods.ADYEN
          )
        ) {
          return true;
        } else {
          this.setError(`The provider has defined that the service must be paid online in advance. Therefore please select the payment method "credit card", "paypal" or "invoice".`, 'Booking Error');
          return false;
        }
      } else {
        if (this.paymentType === this.paymentMethods.CREDITCARD) {
          if (this.bookingService.selectedBookingData.paymentMethodData.stripePaymentMethodId) {
            return true;
          } else {
            this.setError('Stripe token is not valid. Please try again.', 'Booking Error');
            return false;
          }
        } else if (this.paymentType === this.paymentMethods.INVOICE) {
          return true;
        } else if (this.paymentType === this.paymentMethods.STORE) {
          return true;
        } else if (this.paymentType === this.paymentMethods.LEX_OFFICE_INVOICE) {
          return true;
        } else if (this.paymentType === this.paymentMethods.EXTERNAL_INVOICE) {
          return this.isInvoiceAddressComplete;
        } else if (this.paymentType === this.paymentMethods.PAYPAL) {
          if (this.bookingService.selectedBookingData.paymentMethodData.paypalOrderDetail) {
            return true;
          } else {
            return false;
          }
        } else if (this.paymentType === this.paymentMethods.ADYEN) {
          return !!this.bookingService.selectedBookingData.paymentMethodData.adyenPaymentDetails;
        } else {
          this.setError('Please select a valid payment method', 'Booking Error');
          return false;
        }
      }
    }
  }

  setError(message: string, title: string): void {
    this.errorMessage = message;
    this.errorTitle = title;
    this.showError = true;
  }

  resetError(): void {
    this.errorMessage = undefined;
    this.errorTitle = undefined;
    this.showError = false;
  }

  toggleCoupon(): void {
    this.showCouponField = !this.showCouponField;
  }
}
